import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Menu } from 'primereact/menu';
import { Panel } from 'primereact/panel';

import Paginator from '../../../components/Paginator';
import { withAccount } from '../../../components/AccountContext';
import { withGrowl } from '../../../components/GrowlNotifications';
import {
  intlShape, growlShape, locationShape, historyShape,
} from '../../../components/prop-types';
import { parse, sortUrl, toSortOrder } from '../../../utils/UrlUtil';
import AccountInvitationTableHeader from './AccountInvitationTableHeader';

const messages = defineMessages({
  success: { id: 'noti.success', defaultMessage: 'Success!' },
  fail: { id: 'noti.fail', defaultMessage: 'Failed!' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  sendInvitation: { id: 'actions.sennd-invitation', defaultMessage: 'Send Invitation' },
  view: { id: 'actions.view', defaultMessage: 'View' },
  name: { id: 'attrs.name', defaultMessage: 'Name' },
  email: { id: 'attrs.email', defaultMessage: 'Email' },
  type: { id: 'attrs.type', defaultMessage: 'Type' },
  invitedOn: { id: 'attrs.invited.verbose', defaultMessage: 'Invited on' },
  expiresOn: { id: 'attrs.expires.verbose', defaultMessage: 'Expires on' },
});

const invitedTemplate = (rowData, column) => {
  const updated = column.formatDate(moment(rowData.invitedAt), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};

const expiresTemplate = (rowData, column) => {
  const updated = column.formatDate(moment(rowData.expiresAt), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};

function actionsTemplate(rowData, column) {
  const { openConetextMenu } = column;
  return <Button icon="pi pi-ellipsis-h" className="p-button-secondary" onClick={e => openConetextMenu(e, rowData)} />;
}

async function handleSendInvitation(id, props) {
  const {
    sendInvitation,
    currentAccount,
    growl,
    intl: { formatMessage },
  } = props;
  try {
    await sendInvitation.mutation({
      variables: {
        input: {
          accountId: currentAccount,
          invitationId: id,
        },
      },
    });
    growl.show({ severity: 'info', detail: formatMessage(messages.success) });
  } catch (err) {
    growl.show({
      severity: 'error', sticky: true, detail: err.message,
    });
  }
}

async function handleCancelInvitation(id, props) {
  const {
    cancelInvitation,
    currentAccount,
    growl,
    intl: { formatMessage },
  } = props;
  try {
    await cancelInvitation.mutation({
      variables: {
        input: {
          accountId: currentAccount,
          invitationId: id,
        },
      },
    });
    growl.show({ severity: 'info', detail: formatMessage(messages.success) });
  } catch (err) {
    growl.show({ severity: 'error', sticky: true, detail: err.message });
  }
}

function buildMenu(rowData, props) {
  const {
    intl: { formatMessage },
    history,
    currentAccount,
  } = props;

  return [
    {
      label: formatMessage(messages.sendInvitation),
      icon: 'fa fa-paper-plane',
      command: () => handleSendInvitation(rowData.id, props),
    },
    {
      label: formatMessage(messages.cancel),
      icon: 'pi pi-times',
      command: () => handleCancelInvitation(rowData.id, props),
    },
    {
      label: formatMessage(messages.view),
      icon: 'pi pi-arrow-right',
      command: () => {
        const url = `/accounts/${currentAccount}/invitations/${rowData.id}`;

        history.push(url);
      },
    },
  ];
}

function AccountInvitationsTable(props) {
  const {
    intl: { formatMessage, formatDate },
    connection: {
      edges, totalCount, indexFrom, indexTo, pageInfo,
    },
    location,
    history,
    loading,
  } = props;

  const connectionRows = edges.map(({ node }) => ({
    ...node,
  }));

  const [menu, setMenu] = useState([]);
  const [menuRef, setMenuRef] = useState({ toggle: () => null });

  const params = parse(location.search);
  const { sort = {} } = params;
  const { sortBy, sortDir } = sort;

  const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;
  const onSortChange = event => sortUrl(event, location, history);

  const nameHeader = formatMessage(messages.name);
  const emailHeader = formatMessage(messages.email);
  const typeHeader = formatMessage(messages.type);
  const invitedHeader = formatMessage(messages.invitedOn);
  const expiresHeader = formatMessage(messages.expiresOn);

  const openConetextMenu = (event, rowData) => {
    const items = buildMenu(rowData, props);
    setMenu(items);
    menuRef.toggle(event);
  };

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel>
            <div className="p-grid">
              <div className="p-col-12">
                <Menu
                  model={menu}
                  popup
                  ref={(el) => {
                    setMenuRef(el);
                    return el;
                  }}
                  appendTo={document.body}
                />
                <AccountInvitationTableHeader />
              </div>
              <div className="p-col-12 align-center">
                <DataTable
                  value={connectionRows}
                  responsive
                  sortField={sortBy}
                  sortOrder={toSortOrder(sortDir)}
                  onSort={onSortChange}
                  lazy
                  loading={loading}
                  autoLayout
                >
                  <Column field="fullName" header={nameHeader} style={{ textAlign: 'left' }} />
                  <Column field="email" header={emailHeader} />
                  <Column field="type" header={typeHeader} />
                  <Column
                    field="invitedAt"
                    header={invitedHeader}
                    body={invitedTemplate}
                    formatDate={formatDate}
                    sortable
                  />
                  <Column field="expiresAt" header={expiresHeader} body={expiresTemplate} formatDate={formatDate} />
                  <Column body={actionsTemplate} openConetextMenu={openConetextMenu} style={{ width: '3.5em' }} />
                </DataTable>
              </div>
              <div className="p-col-12 align-center">{footer}</div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

AccountInvitationsTable.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  currentAccount: PropTypes.string.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  growl: growlShape.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  sendInvitation: PropTypes.objectOf(PropTypes.any).isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  cancelInvitation: PropTypes.objectOf(PropTypes.any).isRequired,
  loading: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
  location: locationShape.isRequired,
  history: historyShape.isRequired,
};

export default withRouter(withAccount(withGrowl(injectIntl(AccountInvitationsTable))));
